import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import { useSquad } from './data';
import {
  HomeCard,
  HomeEmptyState,
  HomeErrorState,
  HomeSkeletonRows,
  eyebrowSx,
} from './HomeCard';

// "Your squad" — the components owned by the caller's groups (catalog
// ownership relations), with a shortcut to the catalog and the Panda usage tab.
export const YourSquadCard = () => {
  const { squadName, entities, loading, error, retry } = useSquad();
  const first = entities?.[0];

  return (
    <HomeCard
      title="Your squad"
      actions={
        !loading && !error && squadName ? (
          <>
            <Button
              size="small"
              component={RouterLink}
              to="/catalog?filters%5Buser%5D=owned"
            >
              Open in catalog
            </Button>
            {first && (
              <Button
                size="small"
                component={RouterLink}
                to={`/catalog/${
                  first.metadata.namespace ?? 'default'
                }/component/${first.metadata.name}/panda-usage`}
              >
                Panda usage
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      {loading && <HomeSkeletonRows rows={3} />}
      {!loading && error && (
        <HomeErrorState
          title="Couldn't load your squad"
          description="The catalog didn't respond. Try again."
          onRetry={retry}
        />
      )}
      {!loading && !error && !squadName && (
        <HomeEmptyState
          icon={<GroupAddRoundedIcon sx={{ fontSize: 22, opacity: 0.6 }} />}
          title="You're not in a squad yet"
          action={
            <Button
              size="small"
              component={RouterLink}
              to="/catalog?filters%5Bkind%5D=group"
            >
              Browse squads
            </Button>
          }
        >
          Ask your squad lead to add you to your squad's group. Your components
          will show up here.
        </HomeEmptyState>
      )}
      {!loading && !error && squadName && (
        <>
          <Typography component="div" sx={{ ...eyebrowSx, mb: 0.75 }}>
            {squadName}
          </Typography>
          {entities?.map(entity => (
            <Box
              key={entity.metadata.name}
              component={RouterLink}
              to={`/catalog/${
                entity.metadata.namespace ?? 'default'
              }/component/${entity.metadata.name}`}
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1,
                py: 1,
                mx: -1,
                borderRadius: '8px',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.09),
                },
                '&:hover .rowName': { color: 'primary.main' },
              })}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.75,
                }}
              >
                <Typography
                  className="rowName"
                  sx={{
                    fontWeight: 600,
                    fontSize: 13.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {entity.metadata.name}
                </Typography>
                {typeof entity.spec?.type === 'string' && (
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 12, flex: 'none' }}
                  >
                    {entity.spec.type}
                  </Typography>
                )}
              </Box>
              {typeof entity.spec?.lifecycle === 'string' && (
                <Box
                  component="span"
                  sx={theme => ({
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'success.main',
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    borderRadius: '20px',
                    px: 1.1,
                    py: 0.4,
                    flex: 'none',
                  })}
                >
                  {entity.spec.lifecycle}
                </Box>
              )}
            </Box>
          ))}
        </>
      )}
    </HomeCard>
  );
};
